import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AssignmentsTab({ users, lessons, onUpdate }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [exercises, setExercises] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    if (selectedLesson) {
      loadExercises(selectedLesson);
    }
  }, [selectedLesson]);

  useEffect(() => {
    if (selectedUser) {
      loadUserAssignments(selectedUser);
    }
  }, [selectedUser]);

  const loadExercises = async (lessonId) => {
    try {
      const response = await axios.get(`${API}/lessons/${lessonId}/exercises`);
      setExercises(response.data);
    } catch (error) {
      console.error('Error loading exercises:', error);
      toast.error('Failed to load exercises');
    }
  };

  const loadUserAssignments = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/assignments`);
      // Deduplicate assignments by lesson_id (in case of duplicates in DB)
      const uniqueAssignments = response.data.reduce((acc, current) => {
        const existingIndex = acc.findIndex(a => a.lesson_id === current.lesson_id);
        if (existingIndex === -1) {
          acc.push(current);
        }
        return acc;
      }, []);
      setAssignments(uniqueAssignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load assignments');
    }
  };

  const assignLesson = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }
    if (!selectedLesson) {
      toast.error('Please select a lesson');
      return;
    }

    try {
      await axios.post(`${API}/assignments`, {
        user_id: selectedUser,
        lesson_id: selectedLesson
      });
      
      const lesson = lessons.find(l => l.id === selectedLesson);
      toast.success(`Assigned lesson: ${lesson?.title}`);
      loadUserAssignments(selectedUser);
      setSelectedLesson('');
      setExercises([]);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign lesson');
    }
  };

  const unassignLesson = async (lessonId) => {
    try {
      await axios.delete(`${API}/assignments/${selectedUser}/${lessonId}`);
      toast.success('Lesson unassigned');
      loadUserAssignments(selectedUser);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to unassign lesson');
    }
  };

  const isLessonAssigned = (lessonId) => {
    return assignments.some(a => a.lesson_id === lessonId);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Assignment Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Assign Exercises
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Select User */}
          <div>
            <Label>Select User</Label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger data-testid="select-user">
                <SelectValue placeholder="Choose a user..." />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.full_name} (@{user.username})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Select Lesson */}
          <div>
            <Label>Select Lesson</Label>
            <Select value={selectedLesson} onValueChange={setSelectedLesson}>
              <SelectTrigger data-testid="select-lesson">
                <SelectValue placeholder="Choose a lesson..." />
              </SelectTrigger>
              <SelectContent>
                {lessons.map(lesson => (
                  <SelectItem key={lesson.id} value={lesson.id}>
                    {lesson.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lesson Preview */}
          {selectedLesson && (
            <div>
              <Label>About this lesson</Label>
              <div className="mt-2 p-3 bg-gray-50 border rounded-lg">
                <p className="text-sm font-medium mb-2">
                  {lessons.find(l => l.id === selectedLesson)?.title}
                </p>
                {exercises.length > 0 ? (
                  <>
                    <p className="text-xs text-gray-600 mb-2">
                      Contains {exercises.length} exercise{exercises.length !== 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1">
                      {exercises.slice(0, 3).map((ex, i) => (
                        <p key={i} className="text-xs text-gray-500">• {ex.title}</p>
                      ))}
                      {exercises.length > 3 && (
                        <p className="text-xs text-gray-500">...and {exercises.length - 3} more</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">No exercises in this lesson yet</p>
                )}
                {isLessonAssigned(selectedLesson) && (
                  <Badge variant="outline" className="mt-2">
                    <Check className="w-3 h-3 mr-1" />
                    Already Assigned
                  </Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                ℹ️ Any future exercises added to this lesson will automatically appear for this student
              </p>
            </div>
          )}

          {/* Assign Button */}
          <Button
            onClick={assignLesson}
            disabled={!selectedUser || !selectedLesson || isLessonAssigned(selectedLesson)}
            className="w-full"
            data-testid="assign-lesson-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Assign Lesson
          </Button>
        </CardContent>
      </Card>

      {/* Current Assignments */}
      <Card>
        <CardHeader>
          <CardTitle>
            Current Assignments
            {selectedUser && ` (${assignments.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedUser ? (
            <p className="text-center text-gray-400 py-8">Select a user to view their assignments</p>
          ) : assignments.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No assignments yet</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {assignments.map((assignment, index) => {
                const lesson = lessons.find(l => l.id === assignment.lesson_id);
                const bgColor = index % 2 === 0 ? 'bg-blue-50' : 'bg-purple-50';
                return (
                  <Card key={assignment.id} className={bgColor} data-testid={`assignment-item-${index}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-semibold mb-1">
                            {lesson?.title || 'Unknown Lesson'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Entire lesson assigned • All exercises included
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => unassignLesson(assignment.lesson_id)}
                          data-testid={`unassign-btn-${index}`}
                        >
                          Unassign
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
